import type {
  IJsonModel,
  IJsonRowNode,
  IJsonTabNode,
  IJsonTabSetNode,
} from "flexlayout-react";
import {
  freezeWorkspaceProfile,
  type WorkspacePanel,
  type WorkspaceProfile,
} from "./workspace-model";

export const workspaceLayoutLimits = Object.freeze({
  maxDepth: 4,
  maxPanels: 32,
  maxPayloadBytes: 128_000,
  maxTabs: 32,
  minHeight: 240,
  minWidth: 320,
});

export type WorkspaceDiagnosticCode =
  | "DUPLICATE_VIEW_REMOVED"
  | "INVALID_PANEL_REMOVED"
  | "LAYOUT_LIMIT_EXCEEDED"
  | "UNAUTHORIZED_VIEW_REMOVED"
  | "UNKNOWN_VIEW_REMOVED";

export interface WorkspaceDiagnostic {
  readonly code: WorkspaceDiagnosticCode;
  readonly panelId: string | null;
  readonly viewId: string | null;
}

export interface WorkspaceViewPolicy {
  readonly allowClose: boolean;
  readonly allowDetach: boolean;
  readonly allowMove: boolean;
  readonly allowMultiple: boolean;
  readonly minHeight: number;
  readonly minWidth: number;
}

export type WorkspaceViewPolicyResolver = (
  viewId: string,
) => WorkspaceViewPolicy | null;

export interface SanitizedWorkspaceProfile {
  readonly diagnostics: readonly WorkspaceDiagnostic[];
  readonly profile: WorkspaceProfile;
}

function validPanel(panel: WorkspacePanel): boolean {
  return (
    /^[a-zA-Z0-9][a-zA-Z0-9_.-]{0,127}$/.test(panel.id) &&
    /^[a-z][a-z0-9_.-]{0,127}$/.test(panel.panelType) &&
    Number.isFinite(panel.width) &&
    Number.isFinite(panel.height) &&
    panel.configuration.schemaVersion === 1
  );
}

export function sanitizeWorkspaceProfile(
  profile: WorkspaceProfile,
  resolvePolicy: WorkspaceViewPolicyResolver,
): SanitizedWorkspaceProfile {
  const diagnostics: WorkspaceDiagnostic[] = [];
  const seenSingleInstanceViews = new Set<string>();
  const panels: WorkspacePanel[] = [];

  for (const panel of profile.layout.panels.slice(0, workspaceLayoutLimits.maxPanels)) {
    if (!validPanel(panel)) {
      diagnostics.push({ code: "INVALID_PANEL_REMOVED", panelId: panel.id, viewId: panel.panelType });
      continue;
    }
    const policy = resolvePolicy(panel.panelType);
    if (policy === null) {
      diagnostics.push({ code: "UNKNOWN_VIEW_REMOVED", panelId: panel.id, viewId: panel.panelType });
      continue;
    }
    if (!policy.allowMultiple && seenSingleInstanceViews.has(panel.panelType)) {
      diagnostics.push({ code: "DUPLICATE_VIEW_REMOVED", panelId: panel.id, viewId: panel.panelType });
      continue;
    }
    if (!policy.allowMultiple) seenSingleInstanceViews.add(panel.panelType);
    panels.push({
      ...panel,
      height: Math.max(workspaceLayoutLimits.minHeight, policy.minHeight, panel.height),
      width: Math.max(workspaceLayoutLimits.minWidth, policy.minWidth, panel.width),
    });
  }
  if (profile.layout.panels.length > workspaceLayoutLimits.maxPanels) {
    diagnostics.push({ code: "LAYOUT_LIMIT_EXCEEDED", panelId: null, viewId: null });
  }

  return Object.freeze({
    diagnostics: Object.freeze(diagnostics),
    profile: freezeWorkspaceProfile({
      ...profile,
      layout: { ...profile.layout, panels },
    }),
  });
}

export function toFlexLayoutJson(
  profile: WorkspaceProfile,
  resolvePolicy: WorkspaceViewPolicyResolver,
): IJsonModel {
  const sanitized = sanitizeWorkspaceProfile(profile, resolvePolicy).profile;
  const children: IJsonTabNode[] = sanitized.layout.panels.map((panel) => {
    const policy = resolvePolicy(panel.panelType);
    if (policy === null) throw new Error("UNRESOLVED_SANITIZED_VIEW");
    return {
      type: "tab",
      id: panel.id,
      name: panel.panelType,
      component: panel.panelType,
      config: { panelId: panel.id },
      enableClose: policy.allowClose && !panel.locked,
      enableDrag: policy.allowMove && !panel.locked,
      enableFloat: policy.allowDetach && !panel.locked,
      enableRename: false,
    };
  });
  const tabset: IJsonTabSetNode = {
    type: "tabset",
    enableDeleteWhenEmpty: false,
    children,
  };
  const layout: IJsonRowNode = { type: "row", children: [tabset] };
  return { global: {}, borders: [], layout };
}

function collectTabs(
  node: unknown,
  depth: number,
  tabs: IJsonTabNode[],
): void {
  if (depth > workspaceLayoutLimits.maxDepth || tabs.length > workspaceLayoutLimits.maxTabs) {
    throw new Error("LAYOUT_LIMIT_EXCEEDED");
  }
  if (typeof node !== "object" || node === null) throw new Error("INVALID_LAYOUT_JSON");
  const record = node as Record<string, unknown>;
  if (record.type === "tab") {
    if (typeof record.id !== "string" || typeof record.component !== "string") {
      throw new Error("INVALID_LAYOUT_TAB");
    }
    tabs.push(record);
    return;
  }
  if (record.type !== "row" && record.type !== "tabset") throw new Error("UNSUPPORTED_LAYOUT_NODE");
  if (!Array.isArray(record.children)) throw new Error("INVALID_LAYOUT_CHILDREN");
  for (const child of record.children) collectTabs(child, depth + 1, tabs);
}

/** Converts untrusted library JSON back into the library-neutral profile. */
export function fromFlexLayoutJson(
  json: unknown,
  previous: WorkspaceProfile,
  resolvePolicy: WorkspaceViewPolicyResolver,
): SanitizedWorkspaceProfile {
  const serialized = JSON.stringify(json);
  if (serialized.length > workspaceLayoutLimits.maxPayloadBytes) {
    throw new Error("LAYOUT_PAYLOAD_TOO_LARGE");
  }
  if (json === null || typeof json !== "object") {
    throw new Error("INVALID_LAYOUT_JSON");
  }
  const root = json as { readonly layout?: unknown };
  if (root.layout === undefined) throw new Error("INVALID_LAYOUT_JSON");
  const tabs: IJsonTabNode[] = [];
  collectTabs(root.layout, 0, tabs);
  if (tabs.length > workspaceLayoutLimits.maxTabs) throw new Error("LAYOUT_LIMIT_EXCEEDED");

  const byId = new Map(previous.layout.panels.map((panel) => [panel.id, panel]));
  const diagnostics: WorkspaceDiagnostic[] = [];
  const panels: WorkspacePanel[] = [];
  for (const tab of tabs) {
    const viewId = typeof tab.component === "string" ? tab.component : "";
    const prior = byId.get(tab.id ?? "");
    const policy = resolvePolicy(viewId);
    if (policy === null) {
      diagnostics.push({ code: "UNAUTHORIZED_VIEW_REMOVED", panelId: tab.id ?? null, viewId });
      continue;
    }
    if (prior === undefined || prior.panelType !== viewId) {
      diagnostics.push({ code: "INVALID_PANEL_REMOVED", panelId: tab.id ?? null, viewId });
      continue;
    }
    panels.push(prior);
  }
  const sanitized = sanitizeWorkspaceProfile({
    ...previous,
    layout: { ...previous.layout, panels },
  }, resolvePolicy);
  return Object.freeze({
    diagnostics: Object.freeze([...diagnostics, ...sanitized.diagnostics]),
    profile: sanitized.profile,
  });
}
